import { useMutation } from '@apollo/client/react'

import { graphql } from '~publish/graphql'

import { GetTeamMemberCounts } from './useTeamMemberCounts'
import { GetTeamMembers, TEAM_MEMBERS_PAGE_SIZE } from './useTeamMembers'

export const RemoveTeamMember = graphql(/* GraphQL */ `
  mutation RemoveTeamMember($input: RemoveTeamMemberInput!) {
    removeTeamMember(input: $input) {
      ... on EmptySuccess {
        _empty
      }
      ... on CannotRemoveOwnerError {
        message
      }
      ... on LastAdminError {
        message
      }
      ... on NotFoundError {
        message
      }
    }
  }
`)

interface RemoveTeamMemberInput {
  organizationId: string
  accountId: string
}

interface UseRemoveTeamMemberReturn {
  removeTeamMember: (input: RemoveTeamMemberInput) => Promise<{
    success: boolean
    error?: string
  }>
  loading: boolean
}

export const useRemoveTeamMember = (): UseRemoveTeamMemberReturn => {
  const [mutate, { loading }] = useMutation(RemoveTeamMember)

  const removeTeamMember = async (
    input: RemoveTeamMemberInput,
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const { data } = await mutate({
        variables: { input },
        refetchQueries: [
          {
            query: GetTeamMembers,
            variables: {
              input: {
                organizationId: input.organizationId,
                first: TEAM_MEMBERS_PAGE_SIZE,
              },
            },
          },
          // Refresh the Roles summary cards. Passed as a document, not
          // { query }, so it only refetches while RolesTeam has it mounted:
          // legacy organizations share this hook and never issue it.
          GetTeamMemberCounts,
        ],
      })

      if (!data?.removeTeamMember) {
        return { success: false, error: 'Failed to remove team member' }
      }

      const result = data.removeTeamMember

      if (result.__typename === 'EmptySuccess') {
        return { success: true }
      }

      // Handle error responses
      if (
        result.__typename === 'CannotRemoveOwnerError' ||
        result.__typename === 'LastAdminError' ||
        result.__typename === 'NotFoundError'
      ) {
        return { success: false, error: result.message }
      }

      return { success: false, error: 'Unknown error occurred' }
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'An error occurred',
      }
    }
  }

  return {
    removeTeamMember,
    loading,
  }
}
